import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SharedFooter from "@/components/SharedFooter";
import {
  Building2,
  CreditCard,
  LogIn,
  School,
} from "lucide-react";

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header - Consistent with LandingPage */}
      <nav className="relative z-10 px-4 sm:px-6 lg:px-8 py-2 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            <img 
              src="/eduzy-logo-optimized.png" 
              alt="Eduzy" 
              className="h-24 w-auto cursor-pointer"
              style={{ height: '6rem' }}
              data-testid="header-logo"
              onClick={() => navigate("/")}
            />
            <a 
              href="https://contentiumtech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-100 opacity-80 transition-opacity"
            >
              <img 
                src="/contentium-tech-logo.svg" 
                alt="Contentium Tech" 
                className="h-auto"
                style={{ height: '4.5rem' }}
                data-testid="header-contentium-logo"
              />
            </a>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-2">
            <Button
              onClick={() => navigate("/schools")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <School className="w-4 h-4 mr-2" />
              For Schools
            </Button>
            <Button
              onClick={() => navigate("/eduwise")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Pricing
            </Button>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
          {/* Mobile Navigation */}
          <div className="sm:hidden">
            <Button
              onClick={() => navigate("/login")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        </div>
      </nav>

      <main className="flex-1">
        {/* Combined Section: Eduzy by Contentium Tech + Mission + Vision */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            {/* Brand Logos */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
              <div className="text-center">
                <img 
                  src="/eduzy-logo-optimized.png" 
                  alt="Eduzy" 
                  className="h-24 mx-auto"
                />
                <p className="text-emerald-600 font-medium text-sm mt-1">Learn Smart. Learn Easy.</p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-16 h-0.5 bg-emerald-300"></div>
                <span className="text-emerald-600 font-semibold px-4 py-2 bg-emerald-50 rounded-full">
                  A Product Of
                </span>
                <div className="w-16 h-0.5 bg-emerald-300"></div>
              </div>
              
              <div className="text-center">
                <img 
                  src="/contentium-tech-logo.svg" 
                  alt="Contentium Tech" 
                  className="h-20 mx-auto"
                />
              </div>
            </div>

            {/* About Content */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <Building2 className="w-7 h-7 text-emerald-600" />
                Eduzy by Contentium Tech
              </h2>
              
              {/* About Eduzy */}
              <div className="mb-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Eduzy</strong> is an innovative AI-powered educational platform developed and operated by 
                  <strong> Contentium Tech</strong>, a technology-driven content company registered in India 
                  combining content, automation, and AI to build platforms that make learning simple, accessible, and impactful at scale.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Contentium Tech founded Eduzy with a mission to democratize quality education by leveraging 
                  artificial intelligence and WhatsApp's ubiquitous reach to deliver personalized learning 
                  experiences to students across India. The platform adapts to each student's learning pace, 
                  explains concepts in simple language, and makes education engaging through gamification.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Today, Eduzy serves students in classes 5-8 across India, helping them master Mathematics, 
                  Science, English, and more—right from their WhatsApp or our web portal.
                </p>
              </div>

              {/* Mission */}
              <div className="mb-8 p-6 bg-white/70 rounded-xl border border-emerald-100">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  Engineering content with AI, at the speed of curiosity. We combine content, automation, 
                  and AI to create meaningful, scalable platforms that deliver real-world impact—removing 
                  the friction between people and knowledge across devices, classrooms, and digital platforms.
                </p>
              </div>

              {/* Vision */}
              <div className="p-6 bg-white/70 rounded-xl border border-emerald-100">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  From India, to the world. Built in India with a global mindset, we are committed to 
                  creating impactful digital experiences that empower learners, creators, and communities—
                  wherever they are. A coherent ecosystem where structured content, automation, and AI 
                  compound to make knowledge accessible across boundaries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Child's Learning?</h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of students already learning with Eduzy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/login")}
                className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6"
              >
                Start Free Trial
              </Button>
              <Button 
                onClick={() => navigate("/contact")}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </section>

        {/* Legal Entity Information - Separate Section at Bottom */}
        <section className="py-12 px-4 bg-gray-100">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Legal Entity Information</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-gray-600">
                <div>
                  <p><strong>Brand Name:</strong> Eduzy</p>
                  <p><strong>Parent Company:</strong> Contentium Tech</p>
                  <p><strong>GST No:</strong> 07AMDPK8527F1Z1</p>
                </div>
                <div>
                  <p><strong>Registered Address:</strong> New Delhi, India</p>
                  <p><strong>Website:</strong> <a href="https://eduzy.co" className="text-emerald-600 hover:underline">eduzy.co</a></p>
                  <p><strong>Parent Website:</strong> <a href="https://contentiumtech.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">contentiumtech.com</a></p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SharedFooter />
    </div>
  );
}
